import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Servicio para búsqueda de vehículos con filtros avanzados
 */
class SearchService {
  
  /**
   * Buscar vehículos con filtros
   * @param {Object} filters - Objeto con los filtros de búsqueda
   * @param {string} filters.q - Término de búsqueda general
   * @param {number} filters.yearFrom - Año desde
   * @param {number} filters.yearTo - Año hasta
   * @param {string} filters.model - Modelo del vehículo
   * @param {string} filters.brand - Marca del vehículo
   * @param {number} filters.priceMin - Precio mínimo
   * @param {number} filters.priceMax - Precio máximo
   * @param {string} filters.fuel - Tipo de combustible
   * @param {string} filters.transmission - Tipo de transmisión
   * @param {string} filters.region - Región
   * @param {string} filters.city - Ciudad
   * @param {number} filters.page - Página (default: 0, empieza en 0)
   * @param {number} filters.size - Tamaño de página (default: 5)
   */
  async searchVehicles(filters = {}) {
    try {
      // Parámetros por defecto
      const defaultFilters = {
        page: 0,
        size: 5,
        ...filters
      };

      // Construir query parameters
      const queryParams = new URLSearchParams();
      
      Object.entries(defaultFilters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          queryParams.append(key, value);
        }
      });

      const fullUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/search/vehicles/cache?${queryParams.toString()}`;
      
      // Debug logs
      console.log('🔍 SearchService Debug:');
      console.log('📋 Filtros recibidos:', filters);
      console.log('🔧 Filtros procesados:', defaultFilters);
      console.log('🌐 URL completa:', fullUrl);
      console.log('📝 Curl equivalente:', `curl -X 'GET' '${fullUrl}' -H 'accept: application/json'`);

      const response = await api.get(`/api/search/vehicles/cache?${queryParams.toString()}`);
      
      console.log('✅ Respuesta exitosa:', {
        totalElements: response.data.totalElements,
        totalPages: response.data.totalPages,
        contentLength: response.data.content?.length || 0
      });
      
      return response.data;
    } catch (error) {
      console.error('❌ Error al buscar vehículos:', error);
      console.error('🔍 Detalles del error:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      throw error;
    }
  }

  /**
   * Buscar vehículos por marca y modelo
   * @param {string} brand - Marca del vehículo
   * @param {string} model - Modelo del vehículo
   * @param {Object} additionalFilters - Filtros adicionales
   */
  async searchByBrandAndModel(brand, model, additionalFilters = {}) {
    const filters = {
      brand,
      model,
      ...additionalFilters
    };
    
    return this.searchVehicles(filters);
  }

  /**
   * Buscar vehículos por rango de precios
   * @param {number} priceMin - Precio mínimo
   * @param {number} priceMax - Precio máximo
   * @param {Object} additionalFilters - Filtros adicionales
   */
  async searchByPriceRange(priceMin, priceMax, additionalFilters = {}) {
    const filters = {
      priceMin,
      priceMax,
      ...additionalFilters
    };
    
    return this.searchVehicles(filters);
  }

  /**
   * Buscar vehículos por rango de años
   * @param {number} yearFrom - Año desde
   * @param {number} yearTo - Año hasta
   * @param {Object} additionalFilters - Filtros adicionales
   */
  async searchByYearRange(yearFrom, yearTo, additionalFilters = {}) {
    const filters = {
      yearFrom,
      yearTo,
      ...additionalFilters
    };
    
    return this.searchVehicles(filters);
  }

  async getSearchOptions() {
    const response = await api.get('/api/search/options');
    return response.data;
  }

  async getProviders() {
    try {
      const response = await api.get('/api/search/providers');
      return response.data;
    } catch {
      return [];
    }
  }

  /**
   * Búsqueda general con término de búsqueda
   * @param {string} searchTerm - Término de búsqueda
   * @param {Object} additionalFilters - Filtros adicionales
   */
  async generalSearch(searchTerm, additionalFilters = {}) {
    const filters = {
      q: searchTerm,
      ...additionalFilters
    };
    
    return this.searchVehicles(filters);
  }
}

// Crear instancia del servicio
const searchService = new SearchService();

export default searchService;
